// utils/filterUtils.js

/**
 * Optimized brand filtering utilities
 */
export class BrandFilterUtils {
  
  /**
   * Create search index for faster text searching
   */
  static createSearchIndex(brands) {
    return brands.map(brand => ({
      ...brand,
      searchText: [
        brand.name || '',
        brand.description || '',
        brand.welcomeBonus || '',
        ...(brand.features || [])
      ].join(' ').toLowerCase()
    }));
  }

  /**
   * Fast category filtering using pre-computed flags
   */
  static filterByCategory(brands, category) {
    if (category === 'all') return brands;

    return brands.filter(brand => {
      switch (category) {
        case 'top':
          return brand.isTopRated || parseFloat(brand.rating || 0) >= 4.7;
        
        case 'new':
          return brand.isNew || this.isNewBrand(brand);
        
        case 'bonus':
          return brand.hasBestBonus || this.hasBestBonus(brand);
        
        case 'crypto':
          return brand.acceptsCrypto === true;
        
        case 'verified':
          return brand.verified === true;
        
        default:
          return true;
      }
    });
  }

  /**
   * Fast text search using pre-computed search text
   */
  static filterBySearch(brands, searchTerm) {
    if (!searchTerm || !searchTerm.trim()) return brands;

    const searchLower = searchTerm.toLowerCase().trim();
    const searchWords = searchLower.split(/\s+/).filter(word => word.length > 0);

    return brands.filter(brand => {
      const searchText = brand.searchText || this.getSearchText(brand);
      
      // All search words must be found
      return searchWords.every(word => searchText.includes(word));
    });
  }

  /**
   * Combined filtering with performance optimization
   */
  static filterBrands(brands, filters = {}) {
    const { category = 'all', search = '', minRating = 0 } = filters;
    
    let filteredBrands = brands;

    // Apply category filter first (usually more selective)
    if (category !== 'all') {
      filteredBrands = this.filterByCategory(filteredBrands, category);
    }

    // Apply rating filter
    if (minRating > 0) {
      filteredBrands = filteredBrands.filter(brand => 
        parseFloat(brand.rating || 0) >= minRating
      );
    }

    // Apply search filter last (can be expensive)
    if (search && search.trim()) {
      filteredBrands = this.filterBySearch(filteredBrands, search);
    }

    return filteredBrands;
  }

  /**
   * Get search text for a brand
   */
  static getSearchText(brand) {
    return [
      brand.name || '',
      brand.description || '',
      brand.welcomeBonus || '',
      ...(brand.features || [])
    ].join(' ').toLowerCase();
  }

  /**
   * Check if brand is new
   */
  static isNewBrand(brand) {
    if (!brand.createdAt) return false;
    
    try {
      const createdDate = brand.createdAt.toDate ? brand.createdAt.toDate() : new Date(brand.createdAt);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return createdDate > sevenDaysAgo;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if brand has best bonus
   */
  static hasBestBonus(brand) {
    return brand.welcomeBonus && brand.welcomeBonus.includes('No Deposit');
  }

  /**
   * Sort brands by different criteria
   */
  static sortBrands(brands, sortBy = 'rating') {
    const sortedBrands = [...brands];

    switch (sortBy) {
      case 'rating':
        return sortedBrands.sort((a, b) => {
          const ratingA = parseFloat(a.rating) || 0;
          const ratingB = parseFloat(b.rating) || 0;
          return ratingB - ratingA; // Highest first
        });

      case 'newest':
        return sortedBrands.sort((a, b) => {
          if (!a.createdAt || !b.createdAt) return 0;
          
          const dateA = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          
          return dateB - dateA; // Newest first
        });

      case 'name':
        return sortedBrands.sort((a, b) => {
          return (a.name || '').localeCompare(b.name || '');
        });

      case 'bonus':
        return sortedBrands.sort((a, b) => {
          // Prioritize no deposit bonuses
          const hasNoBonusA = this.hasBestBonus(a);
          const hasNoBonusB = this.hasBestBonus(b);
          
          if (hasNoBonusA && !hasNoBonusB) return -1;
          if (!hasNoBonusA && hasNoBonusB) return 1;
          
          // Then by rating
          return parseFloat(b.rating || 0) - parseFloat(a.rating || 0);
        });

      default:
        return sortedBrands;
    }
  }

  /**
   * Get filter statistics
   */
  static getFilterStats(brands, filters = {}) {
    const stats = {
      total: brands.length,
      filtered: 0,
      categories: {
        all: brands.length,
        top: 0,
        new: 0,
        bonus: 0,
        crypto: 0,
        verified: 0
      }
    };

    // Calculate category counts
    brands.forEach(brand => {
      if (brand.isTopRated || parseFloat(brand.rating || 0) >= 4.7) {
        stats.categories.top++;
      }
      if (brand.isNew || this.isNewBrand(brand)) {
        stats.categories.new++;
      }
      if (brand.hasBestBonus || this.hasBestBonus(brand)) {
        stats.categories.bonus++;
      }
      if (brand.acceptsCrypto) {
        stats.categories.crypto++;
      }
      if (brand.verified) {
        stats.categories.verified++;
      }
    });

    // Calculate filtered count
    const filteredBrands = this.filterBrands(brands, filters);
    stats.filtered = filteredBrands.length;

    return stats;
  }
}

export default BrandFilterUtils;